import { useState, useEffect } from "react";
import { useParams } from "react-router";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import { useReduxState } from "../../../../explorer/redux/hooks";
import { Machine, Site } from "../../../../explorer/types/SpareParts";
import { BomItem } from "../../../../explorer/types/SpareParts/BomItem";
import { BomItemDetails } from "../../../../explorer/types/SpareParts/BomItemDetails";
import { Assembly } from "../../../../explorer/types/SpareParts/Assembly";

import {
    AppIconsDocument,
    AppIconsMaintenance,
    AppIconsEmergency,
} from "../../../components/AppIcons";
import BreadcrumbComponent from "../../../components/Breadcrumb/BreadcrumbComponent";
import GoToPage from "../../../components/GoToPage/GoToPage";
import PartList, {
    ColumnAlign,
    ColumnWidth,
} from "../../../components/PartsList/PartList";
import BasketIconComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/BasketIconComponent";
import ItemTextComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/ItemTextComponent";
import OpenNextLevelComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/OpenNextLevelComponent";
import PartSketchThumbnailComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/PartSketchThumbnailComponent";
import ReplaceIconComponent from "../../../components/PartsList/TableLayout/TableColumnComponents/ReplaceIconComponent";
import TreeLayout from "../../../components/PartsList/TreeLayout/TreeLayout";
import { reduxStoreConfig } from "../../../configuration/reduxStoreConfig";
import ServiceProvider from "../../../configuration/ServiceProvider";
import RenderBusinessUnitIcon from "../InstalledBasePage/InstalledBaseAccordion/InstalledBaseAccordionIcon";
import MiniBestView from "./BestView/MiniBestView";
import {
    useMachinePageClasses,
    MachineContainer,
    MachineDetailBar,
    MachineDetailTypography,
    ButtonsContainer,
    MachineContainerBom,
    MachineContent,
    MachineDetailTree,
    HeaderPreview,
    ContainerPreview,
    Icon,
    Preview,
} from "./MachinePage.style";

import useBrands from "../../../../explorer/hooks/useBrands";
import useGoToPage from "../../../hook/useGoToPage";
import { useSearchParams } from "react-router-dom";
import { usePartDetails } from "../../../hook/usePartDetails";
import useLanguages from "../../../../explorer/hooks/useLanguages";
import { TPartListHotSpot } from "../../../../explorer/types/SpareParts/Assembly";
import useCurrentSite from "../../../hook/useCurrentSite";

const MachinePage = () => {
    // Params
    const { customerSiteCode, machineId } = useParams();
    const params = useParams();
    const parentNaturalKey = params["*"];
    const brand = useBrands().currentBrandUrl;

    // State
    const [imageFullScreen, setImageFullScreen] = useState(false);

    const [showDrawing, setShowDrawing] = useState(false);
    const [showPanel, setShowPanel] = useState(false);
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState<BomItemDetails[]>([]);
    const [hotSpots, setHotspots] = useState<TPartListHotSpot[]>([]);
    const [treeItems, setTreeItems] = useState<BomItem[]>([]);
    const [activeDrawing, setActiveDrawing] = useState("");
    const [assembly, setAssembly] = useState<Assembly>();
    const [thumbnails, setThumbnails] = useState<string[]>([]);

    const [selectedDrawingCallout, setSelectedDrawingCallout] = useState<
        string | undefined
    >("");
    const [selectedPartListCallout, setSelectedPartListCallout] = useState<
        string | undefined
    >("");

    const [partDetailsVisible, setPartDetailsVisible] = useState();

    const [selectedPartListPart, setSelectedPartListPart] = useState<
        string | undefined
    >("");
    const lang = useLanguages().currentLanguage;
    //const [lang, setLang] = useState<string>(langHook);

    // ReduxState
    const [currentMachine, setCurrentMachine] = useReduxState<Machine>(
        reduxStoreConfig.sparePartsCurrentMachine
    );
    const [currentSite] = useCurrentSite();

    const { t } = useTranslation();
    const goToPage = useGoToPage();

    const [searchParams] = useSearchParams();
    const showPart = searchParams.get("partCode");

    useEffect(() => {
        setLoading(true);

            ServiceProvider.SpareParts.getMachineById(brand, machineId!)
                .then((result) => setCurrentMachine(result))
                .catch((error) => console.error(error));
        
        ServiceProvider.SpareParts.getMachineBOMById(brand, machineId!, lang)
            .then((result) => {
                setTreeItems(convertToBomItem(result));
                setLoading(false);
            })
            .catch((error) => {
                setLoading(false);
                console.error(error);
            });
    }, [machineId, lang]);

    useEffect(() => {
        if (treeItems && treeItems.length > 0 && parentNaturalKey === "") {
            goToPage.spareParts.goToMachine(
                customerSiteCode!,
                machineId!,
                treeItems[0].id!
            );
        }
        setImageFullScreen(false);
    }, [parentNaturalKey, treeItems]);

    // useEffect(() => {
    //     setLang(langHook);
    // }, [langHook]);

    useEffect(() => {
        if (!parentNaturalKey) return;
        setLoading(true);

        ServiceProvider.SpareParts.getAssembly(
            brand,
            machineId!,
            parentNaturalKey!,
            lang
        )
            .then((result) => {
                setAssembly(result);
                setItems(result.parts);
                if (
                    result.drawings !== undefined &&
                    result.drawings.length > 0
                ) {
                    setShowDrawing(true);
                    setShowPanel(true);
                    setActiveDrawing(result.drawings[0].fileName);
                    const listThumbnails: string[] = [];
                    result.drawings.map((el) => {
                        listThumbnails.push(el.fileName);
                    });
                    setThumbnails(listThumbnails);
                } else {
                    setShowPanel(false);
                    setShowDrawing(false);
                    setActiveDrawing("");
                    setThumbnails([]);
                    setHotspots([]);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
            });
    }, [parentNaturalKey, lang]);

    useEffect(() => {
        if (
            activeDrawing !== undefined &&
            activeDrawing !== "" &&
            assembly !== undefined &&
            assembly?.drawings !== undefined &&
            assembly?.drawings.length > 0
        ) {
            assembly.drawings.map((el) => {
                if (el.fileName === activeDrawing) setHotspots(el.hotspots!);
            });
        }
    }, [activeDrawing]);

    const notifyCalloutPartList = (callout?: string) => {
        setSelectedDrawingCallout(callout);
    };

    const notifyCalloutBestView = (callout?: string) => {
        setSelectedPartListCallout(callout);
    };

    const notifyPartPartList = (part?: string) => {
        setSelectedPartListPart(part);
    };

    // Converte da BomItemDetails a BomItem
    const convertToBomItem = (data: BomItemDetails[]) => {
        const hierarchy: BomItem[] = [];
        const registry: { [naturalKey: string]: BomItem } = {};
        data.sort((i1, i2) => i1.level - i2.level).forEach(
            (item: BomItemDetails) => {
                const bomItem: BomItem = {
                    id: item.naturalKey,
                    label: `${item.tableCode} ${item.description ?? ""}`,
                    children: [],
                    level: item.level,
                    position: item.position
                };
                if (item.level === 1) {
                    hierarchy.push(bomItem);
                } else {
                    var bi = registry[item.parentNaturalKey];
                    if (bi) {
                        bi.children?.push(bomItem);
                        bi.children = bi.children?.sort((a, b) => a.position.padStart(4, "0").localeCompare(b.position.padStart(4, "0")));
                    }
                }
                registry[item.naturalKey] = bomItem;
            }
        );
        return hierarchy;
    };

    const { openPartDetails } = usePartDetails();

    const handelOpenPartDetail = (
        code: string,
        replacementCode: string,
        manufacturer: string,
        position?: string,
        parentNaturalKey?: string,
        item?: any
    ) => {
        if (item.showBasket) {
            openPartDetails(code, replacementCode, manufacturer, position ?? "", parentNaturalKey ? parentNaturalKey.split("/").pop()?.split("_")[0] : "");
        }
    };

    const classes = useMachinePageClasses({
        up: showDrawing,
        showPanel: showPanel,
        imageFullScreen: imageFullScreen,
    });

    return Object.keys(currentMachine).length > 0 ? (
        <MachineContainer>
            <BreadcrumbComponent />
            <MachineDetailBar>
                <div>
                    <MachineDetailTypography variant="h6" p={1}>
                        <RenderBusinessUnitIcon
                            status={currentMachine.businessUnit!}
                        />
                        {`${currentMachine.serialNumber} ${currentMachine.description}`}
                    </MachineDetailTypography>
                    <ButtonsContainer className="osh-hint-navigation-lists">
                        {currentMachine.hasDocument && currentMachine && (
                            <GoToPage
                                page="spareparts.machineMore"
                                tooltipTitle={t(
                                    "common.spareparts.thecnicalDoc"
                                )}
                                children={<AppIconsDocument />}
                                params={{
                                    customerSiteCode:
                                        currentMachine.customerSiteCode!,
                                    machineId: currentMachine.id!.toString(),
                                    tabIndex: "TechnicalDocumentation",
                                }}
                            />
                        )}

                        {currentMachine.hasMaintList && currentMachine && (
                            <GoToPage
                                page="spareparts.machineMore"
                                tooltipTitle={t("common.spareparts.maintList")}
                                children={<AppIconsMaintenance />}
                                params={{
                                    customerSiteCode:
                                        currentMachine.customerSiteCode!,
                                    machineId: currentMachine.id!.toString(),
                                    tabIndex: "MaintenanceList",
                                }}
                            />
                        )}
                        {currentMachine.hasEmergList && currentMachine && (
                            <GoToPage
                                page="spareparts.machineMore"
                                tooltipTitle={t("common.spareparts.emergList")}
                                children={<AppIconsEmergency />}
                                params={{
                                    customerSiteCode:
                                        currentMachine.customerSiteCode!,
                                    machineId: currentMachine.id!.toString(),
                                    tabIndex: "EmergencyList",
                                }}
                            />
                        )}
                    </ButtonsContainer>
                </div>
            </MachineDetailBar>

            <MachineContainerBom
                sx={
                    imageFullScreen
                        ? classes.previewFullscreen
                        : classes.previewOpened
                }
            >
                <MachineContent>
                    <MachineDetailTree className="osh-hint-navigation-tree">
                        <TreeLayout
                            items={treeItems}
                            currentMachine={currentMachine}
                            currentNaturalKey={parentNaturalKey!}
                        />
                    </MachineDetailTree>
                    <main
                        style={{
                            width: "70%",
                            overflow: "auto",
                        }}
                    >
                        <PartList
                            items={items}
                            loading={loading}
                            onRowClick={handelOpenPartDetail}
                            columns={[
                                {
                                    name: "openNextLevel",
                                    viewHeader: false,
                                    width: ColumnWidth.IconWidth,
                                    customComponent: OpenNextLevelComponent,
                                },
                                {
                                    name: "sketchThumbnail",
                                    viewHeader: false,
                                    width: ColumnWidth.IconWidth,
                                    customComponent:
                                        PartSketchThumbnailComponent,
                                },
                                {
                                    name: "position",
                                    viewHeader: true,
                                    width: ColumnWidth.IconWidth,
                                    align: ColumnAlign.Left,
                                },
                                {
                                    name: "code",
                                    viewHeader: true,
                                    width: ColumnWidth.ShortTextWidth,
                                    customComponent: ItemTextComponent,
                                    align: ColumnAlign.Left,
                                },
                                {
                                    name: "description",
                                    viewHeader: true,
                                    width: ColumnWidth.TextWidth,
                                    align: ColumnAlign.Left,
                                },
                                {
                                    name: "withreplacement",
                                    viewHeader: false,
                                    width: ColumnWidth.IconWidth,
                                    customComponent: ReplaceIconComponent,
                                },
                                {
                                    name: "showBasket",
                                    viewHeader: false,
                                    width: ColumnWidth.IconWidth,
                                    customComponent: BasketIconComponent,
                                },
                            ]}
                            calloutNotified={selectedDrawingCallout}
                            notifyCallout={notifyCalloutBestView}
                            notifyPart={notifyCalloutPartList}
                            partNotified={selectedPartListCallout}
                            showPart={showPart !== null ? showPart : undefined}
                        />
                    </main>
                </MachineContent>
                {showPanel && (
                    <Preview>
                        {!imageFullScreen && (
                            <HeaderPreview
                                onClick={(event: any) => {
                                    setShowDrawing(!showDrawing);
                                }}
                            >
                                <Icon
                                    direction={!showDrawing ? "up" : "down"}
                                />
                            </HeaderPreview>
                        )}
                        <ContainerPreview>
                            <MiniBestView
                                imageName={"" + activeDrawing}
                                hotspots={hotSpots}
                                parts={items}
                                thumbnails={thumbnails}
                                setActiveDrawing={setActiveDrawing}
                                onSelect={handelOpenPartDetail}
                                site={currentSite.customerSiteCode!}
                                notifyCallout={notifyCalloutPartList}
                                calloutNotified={selectedPartListCallout}
                                notifyCalloutBestView={notifyCalloutBestView}
                                partNotified={selectedPartListPart}
                                imageFullScreen={imageFullScreen}
                                setImageFullScreen={setImageFullScreen}
                                expandbyWidth={true}
                                maximized={true}
                                up={imageFullScreen}
                            />
                        </ContainerPreview>
                    </Preview>
                )}
            </MachineContainerBom>
        </MachineContainer>
    ) : (
        <></>
    );
};

export default MachinePage;
